// A small router over the History API. Netlify serves index.html for every path (netlify.toml),
// and the app draws the page the path points to.
//
// A link with a #hash, such as /account#billing (Stripe's return from its billing page) or
// /account#emails, opens its page and then scrolls to that section once the page has drawn it.
// Pages draw their sections after their data loads, so the scroll waits for the section to appear,
// and follows it while the page above it is still filling in, until the member scrolls themselves.
// A link without a hash opens at the top.
//
// Paths, queries and hashes (stage 4): usePath() gives the pathname, as the routes need it;
// useLocation() gives the pathname and the query together, so a page that reads ?t= (a recording's
// minute, /funding-engine/week-2?t=08m51s) draws again when only the query changes, as it does when
// a search result or a timestamp link opens a minute on the page already open.
import { useEffect, useState } from 'react'

const listeners = new Set()
const here = () => ({ path: window.location.pathname, search: window.location.search })
const emit = () => listeners.forEach(fn => fn(here()))

// How long a hash waits for its section to appear, and how long the scroll then follows the section
// while the page settles around it
const WAIT_MS = 10000
const SETTLE_MS = 1500

// the member scrolling for themselves ends the follow at once
const USER_SCROLL = ['wheel', 'touchstart', 'keydown', 'mousedown']

let watch = null

function stopWatching() {
  if (!watch) return
  watch.observer.disconnect()
  clearTimeout(watch.timer)
  clearTimeout(watch.settle)
  watch.stopped = true
  for (const type of USER_SCROLL) window.removeEventListener(type, stopWatching, true)
  watch = null
}

function sectionFor(hash) {
  let id = ''
  try { id = decodeURIComponent(hash.replace(/^#/, '')) } catch { id = hash.replace(/^#/, '') }
  return id ? document.getElementById(id) : null
}

// Scrolls to the element the hash names, now or as soon as it's drawn
export function scrollToHash(hash = window.location.hash) {
  stopWatching()
  if (!hash || hash === '#' || typeof MutationObserver === 'undefined' || !document.body) return
  let found = false
  const place = () => {
    const el = sectionFor(hash)
    if (!el) return false
    el.scrollIntoView({ block: 'start' })
    return true
  }
  const onChange = () => {
    if (!place()) return
    if (!found) {
      found = true
      const w = watch
      // the page's fonts landing makes the text above taller without a change to the page, so the
      // follow lasts until they've loaded too (still ended by the member scrolling, or WAIT_MS)
      const fonts = document.fonts && document.fonts.status !== 'loaded' ? document.fonts.ready : Promise.resolve()
      fonts.then(() => {
        if (!w || w.stopped || w !== watch) return
        place()
        w.settle = setTimeout(stopWatching, SETTLE_MS)
      })
    }
  }
  const observer = new MutationObserver(onChange)
  watch = { observer, timer: setTimeout(stopWatching, WAIT_MS), settle: null, stopped: false }
  observer.observe(document.body, { childList: true, subtree: true, characterData: true })
  for (const type of USER_SCROLL) window.addEventListener(type, stopWatching, { capture: true, passive: true })
  onChange()
}

window.addEventListener('popstate', () => {
  emit()
  if (window.location.hash) scrollToHash()
  else stopWatching()
})

// A page opened straight on a hash (a reload, or Stripe sending them back to /account#billing):
// the browser's own jump happens before the app has drawn anything, so do it once the section is there
if (window.location.hash) scrollToHash()

// scroll: false leaves the page where it is, for a page that changes its own address in place (admin's
// filters and chips), where a jump to the top would lose the place
export function navigate(to, { replace = false, scroll = true } = {}) {
  if (/^[a-z][a-z0-9+.-]*:/i.test(to)) {
    replace ? window.location.replace(to) : window.location.assign(to)
    return
  }
  // relative to where they are, so "#emails" stays on this page
  const url = new URL(to, window.location.href)
  const samePage = url.pathname + url.search === window.location.pathname + window.location.search
  if (samePage && replace && url.hash === window.location.hash) return
  window.history[replace ? 'replaceState' : 'pushState']({}, '', url.pathname + url.search + url.hash)
  if (url.hash && scroll) {
    if (!samePage) window.scrollTo(0, 0)
    scrollToHash(url.hash)
  } else {
    stopWatching()
    if (scroll) window.scrollTo(0, 0)
  }
  emit()
}

// { path, search }, kept in step with the address bar
function useHere() {
  const [loc, setLoc] = useState(here)
  useEffect(() => {
    const update = next => setLoc(prev => (prev.path === next.path && prev.search === next.search ? prev : next))
    listeners.add(update)
    // the address may have moved between the first draw and this effect
    update(here())
    return () => listeners.delete(update)
  }, [])
  return loc
}

export function usePath() {
  return useHere().path
}

// { path, search, query }: query is a URLSearchParams of the address's query
export function useLocation() {
  const { path, search } = useHere()
  return { path, search, query: new URLSearchParams(search) }
}

// 1 value from the address's query, or null: useQueryParam('t')
export function useQueryParam(name) {
  return new URLSearchParams(useHere().search).get(name)
}

// An <a> that moves within the app without a reload. Links out (any address with a scheme, such as
// https: or mailto:), and clicks with a modifier key, behave as links always do.
export function Link({ to, onClick, children, ...rest }) {
  const external = /^[a-z][a-z0-9+.-]*:/i.test(to)
  function handle(e) {
    if (onClick) onClick(e)
    if (e.defaultPrevented || external || rest.target || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
    e.preventDefault()
    navigate(to)
  }
  return <a href={to} onClick={handle} {...rest}>{children}</a>
}
